/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import { Texture, BaseTexture, GraphicsGeometry, SCALE_MODES, Resource } from 'pixi.js-legacy';
import { CreateTextureInterface } from '../../canvas-common/interfaces/create-texture.interface';
import { RendererCache } from './RendererCache';
import { TICKET_BORDER_COLORS } from '../ticket-components/TicketSelected';

/**
 * This class renders the basic ticket shape. Also has caching ability. Don't create too many instances of this.
 */
export class TaskRenderer extends RendererCache {
	private static defaultResolution: number = 1;
	/**
	 * @param {boolean} nativeLines If true the lines will be draw using LINES instead of TRIANGLE_STRIP
	 * @param {CreateTextureInterface} options CreateTextureInterface
	 */
	constructor(geometry?: GraphicsGeometry, options?: CreateTextureInterface) {
		super(geometry);
		this.createTexture(options);

		this.generateRoundedTexture();

		const workDayTexture = {
			id: 'workDayShaded',
			outlineColor: '#777777',
			outlineWidth: 1,
		};
		this.createTexture(workDayTexture);

		this.createMeshes();
	}
	/**
	 * Generates a rectangular shape and returns a {@link PIXI.Texture}
	 * @param {CreateTextureInterface} options CreateTextureInterface
	 * @returns {PIXI.Texture}
	 */
	public createTexture(options: CreateTextureInterface = {}): TaskRenderer {
		const x = options.x || 0;
		const y = options.y || 0;
		const tAlpha = options.alpha || 1;
		const tWidth = options.width || 145;
		const tHeight = options.height || 135;
		const tColor = options.color ? parseInt(options.color.substring(1), 16) : 0xffffff;
		const tLineColor = options.outlineColor ? parseInt(options.outlineColor.substring(1), 16) : 0xffffff;
		const tLineWidth = options.outlineWidth || 0;
		const tLineAlpha = options.outlineAlpha || 1;
		const id = options.id || 'ticket';
		// Clear drawing buffer
		this.clear();
		// Draw the ticket shape
		this.lineStyle(tLineWidth, tLineColor, tLineAlpha);
		this.beginFill(tColor, tAlpha);
		this.drawRect(x, y, tWidth, tHeight);
		this.endFill();
		// Cache texture
		this.cacheTexture(
			id,
			this.generateCanvasTexture(options.scaleMode, options.resolution) as Texture<Resource>
		);

		return this;
	}
	/**
	 * This generates the ticket mesh containing pre-baked shadows, and the ticket outline mesh. Much more efficient doing this than it is applying shadows or an outline as a post-processing effect, since shadows are very expensive.
	 * @returns CanvasRenderer.TaskRenderer
	 */
	public createMeshes() {
		// Create and cache the ticket shadow mesh
		this.cacheTexture('ticketShadowMesh', this.generateTicketShadowMesh());
		// We need to generate the task outline in all of the different colors we need. This is because PIXI.Mesh.tint does not support
		// the CanvasRenderer....
		// See: https://github.com/pixijs/pixi.js/issues/3862
		this.cacheTexture('ticketOutlineMesh', this.generateTicketOutlineMesh());
		this.cacheTexture(
			'ticketOutlineSelectedMesh',
			this.generateTicketOutlineMesh(TICKET_BORDER_COLORS.SELECTED)
		);
		this.cacheTexture(
			'ticketOutlineCycleSelectedMesh',
			this.generateTicketOutlineMesh(TICKET_BORDER_COLORS.CYCLE_SELECTED)
		);
		this.cacheTexture(
			'ticketOutlineErrorMesh',
			this.generateTicketOutlineMesh(TICKET_BORDER_COLORS.OUT_OF_SEQUENCE)
		);

		const roundedCheck: Texture = this.generateRoundedCheckboxTexture();
		this.cacheTexture('roundedCheckbox', roundedCheck);
		return this;
	}
	/**
	 * Generates a mesh representing the ticket shadow.
	 * @returns {PIXI.Texture}
	 */
	private generateTicketShadowMesh() {
		this._scratchContext.save();
		// Clear scratch canvas
		this._scratchContext.clearRect(0, 0, this._scratchCanvas.width, this._scratchCanvas.height);
		// Size the scratch canvas a bit larger than ticket size to account for the shadow drawing
		this._scratchCanvas.width = 190;
		this._scratchCanvas.height = 180;

		// Create the task size
		this._scratchContext.rect(10, 10, 147, 137);

		this._scratchContext.fillStyle = 'white';
		this._scratchContext.shadowColor = '#141414'; // because Davina doesn't like black
		this._scratchContext.shadowBlur = 10;
		this._scratchContext.shadowOffsetX = 0;
		this._scratchContext.shadowOffsetY = 2;
		this._scratchContext.fill();

		// Generate a texture
		return new Texture(new BaseTexture(this.getCanvasTexture()));
	}

	/**
	 * Generate the rounded texture for Task.isLongest
	 */
	public generateRoundedTexture(): Texture {
		this.clear();
		this.beginFill(0xffffff, 1);
		this.drawRoundedRect(0, 0, 60, 35, 10);
		this.endFill();

		const texture: Texture<Resource> = this.generateCanvasTexture(
			SCALE_MODES.NEAREST,
			TaskRenderer.defaultResolution
		) as Texture<Resource>;
		this.cacheTexture('isLongestTexture', texture);
		return texture;
	}
	/**
	 * Generates a mesh representing the ticket outline.
	 * @return {PIXI.Texture}
	 */
	private generateTicketOutlineMesh(color = 'white', lineWidth = 40) {
		// Clear scratch canvas
		this._scratchContext.save();
		this._scratchContext.clearRect(0, 0, this._scratchCanvas.width, this._scratchCanvas.height);
		// Resize our scratch canvas
		this._scratchCanvas.width = 135;
		this._scratchCanvas.height = 125;
		// Draw stroke outline. We draw it as white, since we will be tinting this mesh to any color later.
		this._scratchContext.lineWidth = lineWidth;
		// this._scratchContext.fillStyle = color;
		// this._scratchContext.fillRect(0,0,145,135);

		this._scratchContext.strokeStyle = color;
		this._scratchContext.rect(0, 0, 135, 125);
		this._scratchContext.stroke();

		this._scratchContext.restore();
		// Generate a texture
		return new Texture(new BaseTexture(this.getCanvasTexture()));
	}
	/**
	 * MOC-3960 - Generate rounded texture for checkbox
	 */
	public generateRoundedCheckboxTexture(): Texture {
		// Clear scratch canvas
		this._scratchContext.save();
		this._scratchContext.clearRect(0, 0, this._scratchCanvas.width, this._scratchCanvas.height);

		// Resize our scratch canvas
		this._scratchCanvas.width = 120;
		this._scratchCanvas.height = 120;

		let radius = 20;
		const ctx = this._scratchContext;
		const x = 0;
		const y = 0;
		const width = 120;
		const height = 120;

		if (width < 2 * radius) {
			radius = width / 2;
		}
		if (height < 2 * radius) {
			radius = height / 2;
		}
		ctx.beginPath();
		ctx.moveTo(x + radius, y);
		ctx.arcTo(x + width, y, x + width, y + height, radius);
		ctx.arcTo(x + width, y + height, x, y + height, radius);
		ctx.arcTo(x, y + height, x, y, radius);
		ctx.arcTo(x, y, x + width, y, radius);
		ctx.closePath();

		ctx.strokeStyle = '#a2a2a2';
		ctx.fillStyle = '#e7e7e7';
		ctx.lineJoin = 'round';
		ctx.lineCap = 'round';
		ctx.fill();
		// ctx.stroke();

		this._scratchContext.restore();
		// Generate a texture
		return new Texture(new BaseTexture(this.getCanvasTexture()));
	}
}
